import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { crewOrderContext } from '~/app/features/order-middleware/orderMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import { AppStore } from '~/stores/app';
import api from '~/utils/api';
import { OrderEditType } from '~/utils/constants';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import {
  checkUsPostalCode,
  customerInfoSchema,
  orderEditActionSchema,
} from '~/utils/schemas';
import { AsyncReturnType } from '~/utils/types';

export const editActionTypeFormKey = 'editType';

export const QUANTITY_INPUT_PREFIX = 'qty:';
export const QUANTITY_ORIGINAL_PREFIX = 'qty-original:';

export const SELECTED_SHIPPING_LINE_TITLE_KEY = 'selectedShippingLineTitle';

export const DISCOUNT_CODE_KEY = 'code';

// TODO: enforce all keys
const formSchema = zfd.formData(
  z.discriminatedUnion(editActionTypeFormKey, [
    z
      .object(
        {
          [editActionTypeFormKey]: z.literal(
            OrderEditType.UPDATE_SHIPPING_ADDRESS,
          ),
        },
        {
          message:
            'Something is not quite right with the shipping address. Please check your address and try again.',
        },
      )
      .merge(customerInfoSchema.omit({ email: true }))
      .check(checkUsPostalCode),

    z.object({
      [editActionTypeFormKey]: z.literal(OrderEditType.CANCEL_ORDER),
    }),

    z.object({
      [editActionTypeFormKey]: z.literal(OrderEditType.MODIFY_QUANTITY),
    }),

    z.object({
      [editActionTypeFormKey]: z.literal(OrderEditType.UPDATE_EMAIL),
      email: zfd.text(
        z.email({ message: 'Please enter a valid email address.' }),
      ),
    }),

    z.object({
      [editActionTypeFormKey]: z.literal(OrderEditType.UPDATE_SHIPPING_LINE),
      [SELECTED_SHIPPING_LINE_TITLE_KEY]: zfd.text(
        z.string({ message: 'Please select a shipping method.' }),
      ),
    }),

    z.object({
      [editActionTypeFormKey]: z.literal(OrderEditType.ADD_DISCOUNT_CODE),
      [DISCOUNT_CODE_KEY]: zfd.text(
        z
          .string({ message: 'Please enter a discount code.' })
          .trim()
          .min(1, { message: 'Please enter a discount code.' })
          .max(64),
      ),
    }),
  ]),
);

const extractLineItemDeltas = (formData: FormData) =>
  [...formData.entries()]
    .filter(([key]) => key.startsWith(QUANTITY_INPUT_PREFIX))
    .map(([key, value]) => {
      const id = key.slice(QUANTITY_INPUT_PREFIX.length);
      const originalRaw = formData.get(`${QUANTITY_ORIGINAL_PREFIX}${id}`);
      return {
        storeOrderLineItemId: Number(id),
        newQuantity: Number(value),
        previousQuantity:
          typeof originalRaw === 'string' ? Number(originalRaw) : NaN,
      };
    })
    .filter(
      ({ storeOrderLineItemId, newQuantity, previousQuantity }) =>
        Number.isFinite(storeOrderLineItemId) &&
        Number.isFinite(newQuantity) &&
        Number.isFinite(previousQuantity) &&
        newQuantity !== previousQuantity,
    );

const extractBody = (
  data: z.infer<typeof formSchema>,
  editAction: z.infer<typeof orderEditActionSchema> | null,
  formData: FormData,
) =>
  match({ data, currentAction: editAction })
    .with(
      {
        data: { editType: 'UPDATE_SHIPPING_ADDRESS' },
        currentAction: { editType: 'UPDATE_SHIPPING_ADDRESS' },
      },
      ({
        data: { firstName, lastName, address, editType },
        currentAction,
      }) => ({
        editType,
        action: currentAction,
        stagedEdits: {
          updatedShippingAddress: {
            ...address,
            firstName,
            lastName,
            stateOrProvinceCode: address.stateOrProvinceCode ?? undefined,
            phone: address.phone ?? undefined,
          },
        },
      }),
    )
    .with(
      {
        data: { editType: 'CANCEL_ORDER' },
        currentAction: { editType: 'CANCEL_ORDER' },
      },
      ({ data: { editType }, currentAction }) => ({
        editType,
        action: currentAction,
      }),
    )
    .with(
      {
        data: { editType: 'MODIFY_QUANTITY' },
        currentAction: { editType: 'MODIFY_QUANTITY' },
      },
      ({ data: { editType }, currentAction }) => ({
        editType,
        action: currentAction,
        stagedEdits: {
          // server overwrites these from its calculatedOrder; placeholder values
          originalTotal: { amount: '0', currencyCode: 'USD' },
          calculatedTotal: { amount: '0', currencyCode: 'USD' },
          additionalPaymentCollectionUrl: null,
          lineItemDeltas: extractLineItemDeltas(formData),
        },
      }),
    )
    .with(
      {
        data: { editType: 'UPDATE_EMAIL' },
        currentAction: { editType: 'UPDATE_EMAIL' },
      },
      ({ data: { editType, email }, currentAction }) => ({
        editType,
        action: currentAction,
        stagedEdits: { email },
      }),
    )
    // TODO: I don't like us having to set default stagedEdits here wtf
    .with(
      {
        data: { editType: 'UPDATE_SHIPPING_LINE' },
        currentAction: { editType: 'UPDATE_SHIPPING_LINE' },
      },
      ({ data: shippingLineData, currentAction }) => ({
        editType: shippingLineData.editType,
        action: currentAction,
        stagedEdits: {
          selectedShippingLine: {
            title: shippingLineData[SELECTED_SHIPPING_LINE_TITLE_KEY],
            price: '0',
          },
          previousShippingLine: { title: '', price: '0' },
          // server overwrites these from its calculatedOrder; placeholder values
          originalTotal: { amount: '0', currencyCode: 'USD' },
          calculatedTotal: { amount: '0', currencyCode: 'USD' },
          additionalPaymentCollectionUrl: null,
        },
      }),
    )
    .with(
      {
        data: { editType: 'ADD_DISCOUNT_CODE' },
        currentAction: { editType: 'ADD_DISCOUNT_CODE' },
      },
      ({ data: discountData, currentAction }) => ({
        editType: discountData.editType,
        action: currentAction,
        stagedEdits: {
          code: discountData[DISCOUNT_CODE_KEY],
          // server overwrites these from the calculator response
          originalTotal: { amount: '0', currencyCode: 'USD' },
          calculatedTotal: { amount: '0', currencyCode: 'USD' },
          refundAmount: { amount: '0', currencyCode: 'USD' },
        },
      }),
    )
    .otherwise(() => {
      throw new Error('Invalid edit action');
    });

const handleStageResponse =
  (appStore: AppStore) =>
  (stagedEdit: AsyncReturnType<typeof api.stageOrderEdit>) => {
    // handle error case
    if (!stagedEdit.ok) {
      return {
        ok: false as const,
        message: stagedEdit.message,
      };
    }
    appStore.dispatch(appStore.actions.orderEdit.assignEdits(stagedEdit));

    return redirect(`../commit`);
  };

export const { action, useActionResult } = makeAction(
  async ({ context, formData }, appStore) => {
    const { orderEdit } = appStore.getState();
    const settings = context.get(settingsContext);
    const order = context.get(crewOrderContext);

    return (
      match(formSchema.safeParse(formData))
        // TODO: verify redirects / clear state if necessary
        .with({ success: false }, ({ error }) => ({
          ok: false,
          message: error.message,
        }))
        .with({ success: true }, ({ data }) => {
          const body = extractBody(data, orderEdit.action, formData);
          if (
            body.editType === 'MODIFY_QUANTITY' &&
            body.stagedEdits.lineItemDeltas.length === 0
          ) {
            return {
              ok: false as const,
              message:
                'No quantity changes were made. Please adjust at least one item quantity to proceed.',
            };
          }
          return api
            .stageOrderEdit({
              params: {
                storefrontId: settings.storefrontId,
                storeOrderId: order.id,
              },
              body,
            })
            .then(handleStageResponse(appStore));
        })
        .exhaustive()
    );
  },
);

export const { loader, useLoaderData } = makeLoader(
  async ({ context }, appStore) => {
    const settings = context.get(settingsContext);
    const order = context.get(crewOrderContext);
    return (
      match(appStore.getState().orderEdit)
        .with({ step: P.not(P.union('editing', 'reviewing')) }, () =>
          redirect('../'),
        )
        .with({ editType: 'UPDATE_SHIPPING_ADDRESS' }, (state) => ({
          editType: state.action.editType,
          shippingAddress: order.shippingAddress,
          action: state.action,
          headline: 'Update Shipping Address',
        }))
        // Add to the union for types that don't require staging
        .with({ editType: 'CANCEL_ORDER' }, (state) =>
          api
            .stageOrderEdit({
              params: {
                storefrontId: settings.storefrontId,
                storeOrderId: order.id,
              },
              body: {
                editType: state.action.editType,
                action: state.action,
              },
            })
            .then(handleStageResponse(appStore)),
        )
        .with({ editType: 'REMOVE_PACKAGE_PROTECTION' }, (state) =>
          api
            .stageOrderEdit({
              params: {
                storefrontId: settings.storefrontId,
                storeOrderId: order.id,
              },
              body: {
                editType: state.action.editType,
                action: state.action,
              },
            })
            .then(handleStageResponse(appStore)),
        )
        .with({ editType: 'MODIFY_QUANTITY' }, (state) => ({
          editType: state.action.editType,
          action: state.action,
          lineItems: order.lineItems,
          headline: 'Adjust Product Quantity',
        }))
        .with({ editType: 'UPDATE_EMAIL' }, (state) => ({
          editType: state.action.editType,
          currentEmail: order.email,
          action: state.action,
          headline: 'Update Email',
        }))
        .with({ editType: 'UPDATE_SHIPPING_LINE' }, (state) => {
          if (!state.prepared) return redirect('../');

          return {
            editType: state.action.editType,
            action: state.action,
            quotes: state.prepared.quotes,
            headline: 'Change Shipping Method',
          };
        })
        .with({ editType: 'ADD_DISCOUNT_CODE' }, (state) => ({
          editType: state.action.editType,
          action: state.action,
          headline: 'Apply Discount Code',
        }))
        .exhaustive()
    );
  },
);
